/**
 * PersonaSelectionScreen
 *
 * Inline persona selection UI rendered inside the ChatbotPanel body when the
 * user has not yet chosen a persona. Displays two side-by-side cards (Pro and
 * Guide) with icon, name, and description. On card click the parent's
 * `onSelect` callback is invoked to persist the choice via the Preference API.
 *
 * Requirements: 3.1–3.6, 4.1, 4.2, 4.4, 4.5, 9.1–9.4
 */

import { useState } from 'react';
import { Rocket, Compass, Loader2 } from 'lucide-react';

const PERSONAS = [
  {
    type: 'pro',
    name: 'Pro',
    Icon: Rocket,
    description: 'Direct answers with code examples',
    ariaLabel: 'Select Pro persona — technical, concise, for developers',
    accentColor: 'blue',
  },
  {
    type: 'guide',
    name: 'Guide',
    Icon: Compass,
    description: 'Simple explanations with walkthroughs',
    ariaLabel: 'Select Guide persona — friendly, step-by-step, for everyone',
    accentColor: 'green',
  },
];

/**
 * @param {Object} props
 * @param {(agentType: 'pro' | 'guide') => Promise<void>} props.onSelect
 */
export function PersonaSelectionScreen({ onSelect }) {
  const [selecting, setSelecting] = useState(null);
  const [error, setError] = useState(null);

  const handleSelect = async (type) => {
    if (selecting) return;
    setSelecting(type);
    setError(null);
    try {
      await onSelect(type);
    } catch (err) {
      setError('Something went wrong. Please try again.');
      setSelecting(null);
    }
  };

  return (
    <section className="flex-1 flex flex-col items-center justify-center px-4 py-6 bg-[#0f1117]">
      {/* Welcome heading */}
      <h2 className="text-lg font-semibold text-gray-100 mb-1 text-center">
        Welcome to Imago AI
      </h2>
      <p className="text-xs text-gray-400 mb-5 text-center">
        Choose your assistant style
      </p>

      {/* Persona cards */}
      <div className="grid grid-cols-2 gap-3 w-full max-w-[280px] persona-card-grid">
        {PERSONAS.map(({ type, name, Icon, description, ariaLabel, accentColor }) => {
          const isSelecting = selecting === type;
          const isDisabled = !!selecting;

          const accentBorder =
            accentColor === 'blue' ? 'border-blue-500' : 'border-green-500';
          const accentHover =
            accentColor === 'blue'
              ? 'hover:border-blue-400/70'
              : 'hover:border-green-400/70';
          const iconBg =
            accentColor === 'blue'
              ? 'bg-blue-500/10 text-blue-400'
              : 'bg-green-500/10 text-green-400';

          return (
            <button
              key={type}
              onClick={() => handleSelect(type)}
              disabled={isDisabled}
              aria-label={ariaLabel}
              aria-busy={isSelecting}
              className={`
                relative flex flex-col items-center text-center p-3 rounded-lg
                border transition-all duration-200
                ${isSelecting ? accentBorder : 'border-gray-700/50'}
                ${!isDisabled ? `${accentHover} hover:bg-white/[0.03] cursor-pointer` : ''}
                disabled:opacity-60 disabled:cursor-wait
                focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:ring-offset-1 focus:ring-offset-[#0f1117]
              `}
            >
              <div
                className={`w-9 h-9 rounded-full flex items-center justify-center mb-2 ${iconBg}`}
              >
                {isSelecting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Icon className="w-4 h-4" />
                )}
              </div>
              <h3 className="text-sm font-medium text-gray-100 mb-0.5">{name}</h3>
              <p className="text-label text-gray-500 leading-snug">{description}</p>
            </button>
          );
        })}
      </div>

      {/* Error message */}
      {error && (
        <p className="mt-4 text-sm text-red-400 text-center" role="alert">
          {error}
        </p>
      )}

      {/* Responsive style — stack cards when panel is narrow */}
      <style>{`
        @container (max-width: 399px) {
          .persona-card-grid {
            grid-template-columns: 1fr;
          }
        }
        @media (max-width: 450px) {
          .persona-card-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </section>
  );
}

export default PersonaSelectionScreen;
